import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { FadeUp, EYEBROW, GRAD_TEXT } from './shared';

const FAQS = [
  {
    q: 'Is the assessment really free?',
    a: 'Yes. Uploading your CV and receiving your country-by-country assessment costs nothing. If you want expert help acting on it, you can book a free consultation with an Axpats consultant afterwards.',
  },
  {
    q: 'How accurate are the results?',
    a: 'Our engine is built on published immigration criteria from official government sources and is reviewed regularly. Your results show what is confirmed from your CV versus what still needs verification — no inflated promises.',
  },
  {
    q: 'What happens to my CV and personal data?',
    a: 'Your CV is encrypted in transit and at rest, used only to generate your assessment, and never sold or shared with third parties.',
  },
  {
    q: 'I don\u2019t have an English test yet. Can I still get assessed?',
    a: 'Absolutely. Tell us your approximate English level and we\u2019ll assess you with that — your results will show exactly how much an official test would strengthen your options.',
  },
  {
    q: 'What if I\u2019m not sure which country I want?',
    a: 'That\u2019s exactly what the assessment is for. You\u2019ll see every country where your profile is strongest, ranked, so you can compare before committing to anything.',
  },
];

const FaqItem = ({ faq, index, open, onToggle }) => (
  <div
    data-testid={`faq-item-${index}`}
    style={{
      border: '1px solid rgba(48,43,44,0.1)', borderRadius: '2px', background: '#fff',
      overflow: 'hidden', transition: 'border-color 0.3s',
      ...(open ? { borderColor: 'rgba(246,116,37,0.35)' } : {}),
    }}
  >
    <button
      onClick={onToggle}
      data-testid={`faq-toggle-${index}`}
      style={{
        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '1rem', padding: '1.25rem 1.5rem', background: 'none', border: 'none', cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 700, color: '#302B2C' }}>{faq.q}</span>
      <span style={{ color: '#F67425', flexShrink: 0 }}>{open ? <Minus size={17} /> : <Plus size={17} />}</span>
    </button>
    {open && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        style={{ overflow: 'hidden' }}
      >
        <p style={{
          padding: '0 1.5rem 1.25rem', margin: 0,
          fontFamily: 'var(--font-body)', fontSize: '0.9rem',
          color: 'rgba(48,43,44,0.72)', lineHeight: 1.7,
        }}>
          {faq.a}
        </p>
      </motion.div>
    )}
  </div>
);

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section data-testid="faq-section" style={{ padding: '0 1.5rem 7rem', maxWidth: '820px', margin: '0 auto' }}>
      <FadeUp>
        <div style={{ marginBottom: '2.5rem' }}>
          <span style={EYEBROW}>FAQ</span>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 800, color: '#302B2C', letterSpacing: '-0.03em', lineHeight: 1.05, marginTop: '0.75rem' }}>
            Questions, <span style={GRAD_TEXT}>answered</span>
          </h2>
        </div>
      </FadeUp>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {FAQS.map((faq, i) => (
          <FadeUp key={faq.q} delay={i * 0.06}>
            <FaqItem faq={faq} index={i} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? null : i)} />
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

export default FAQ;
